import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

function CrownsPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Where does the Crowns idea come from?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Crowns is inspired by a classic chessboard placement problem, using crowns and region constraints to create a pure logic puzzle.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Can crowns touch each other?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Crowns cannot be placed in adjacent cells, including diagonally, which creates exclusion zones that drive most deductions.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do I need to guess?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'No. Each puzzle has a unique solution that can be reached through logical reasoning.'
        }
      }
    ]
  };

  const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Crowns",
      "description": "A region logic puzzle in the chessboard-placement tradition. Place crowns so no two share a row, column, region, or touch diagonally.",
      "url": "https://logicpuzzles.ca/games/crowns",
      "image": "https://logicpuzzles.ca/assets/images/gameplay/crowns.png",
      "genre": "Logic Puzzle",
      "gamePlatform": "Web Browser",
      "operatingSystem": "Any (browser-based)",
      "playMode": "SinglePlayer",
      "inLanguage": "en",
      "isAccessibleForFree": true,
      "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock"
      },
      "publisher": {
          "@id": "https://logicpuzzles.ca/#organization"
      },
      "potentialAction": {
          "@type": "PlayAction",
          "target": "https://logicpuzzles.ca/play/crowns"
      }
  };

  return (
    <div>
      <Helmet>
        <title>Crowns Logic Puzzle - Pure Logic Sudoku-Style Grid Puzzle | Logic Puzzles CA</title>
        <meta name="description" content="Solve Crowns logic puzzles online! Pure deductive reasoning grid puzzle with unique solution. No guessing required - perfect for hardcore logic puzzle fans." />
        <meta name="keywords" content="Crowns logic puzzle, deductive reasoning puzzle, grid logic puzzle, sudoku style puzzle, constraint satisfaction puzzle, pure logic puzzle, mathematical puzzle, logical deduction game" />
        <meta property="og:title" content="Crowns Logic Puzzle - Play Free Online" />
        <meta property="og:description" content="Place one crown in every row, column and color region, with no two crowns touching. Play Crowns free online." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/assets/images/gameplay/crowns.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Crowns Logic Puzzle - Play Free Online" />
        <meta name="twitter:description" content="One crown per row, column and color region, and crowns can't touch. Play Crowns free online." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/assets/images/gameplay/crowns.png" />
        <link rel="canonical" href="https://logicpuzzles.ca/games/crowns" />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs categoryName="Logic Games" categoryPath="/logic-games" currentLabel="Crowns" />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">Logic Puzzle · Free online</p>
            <h1>Crowns Logic Puzzle</h1>
            <p className="gi-hero__lead">Pure deductive reasoning grid challenge</p>
            <div className="gi-hero__actions">
              <Link to="/play/crowns" className="button">▶ Play now</Link>
              <a href="#how-to-play" className="button secondary">How to play</a>
              <Link to="/printable/crowns" className="button secondary">Printable puzzles</Link>
            </div>
            <p className="gi-hero__note">
              <Link to="/crowns-puzzle"><strong>Play today’s daily Crowns puzzle</strong></Link> or try unlimited Crowns puzzles in your browser.
            </p>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src="/crowns/icons/Icon-192.png" alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        <StoreCTA app="crowns" placement="game-page" page="/games/crowns" headline="Play Crowns on your phone with the free app" />

        <section className="gi-section">
          <p className="gi-lead">
            <strong>Crowns logic puzzle</strong> is a <strong>pure logic puzzle</strong> that challenges
            your <strong>deductive reasoning</strong> skills through systematic constraint satisfaction.
            This <strong>grid logic puzzle</strong> follows the same principles as sudoku - every puzzle
            has exactly one solution that can be reached through logical deduction alone, with no
            guessing required. Perfect for hardcore <strong>logic puzzle</strong> enthusiasts who
            demand mathematical precision and sound puzzle design.
          </p>
        </section>

        <section className="gi-section" id="how-to-play">
          <h2>How to Play Crowns Logic Puzzle</h2>
          <div className="gi-grid">
            <div className="gi-card">
              <span className="gi-card__num">01</span>
              <h3>Objective</h3>
              <p>Place exactly one crown in every row, every column and every color region of the grid.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">02</span>
              <h3>Crown Placement</h3>
              <p>Tap a cell once to mark it with an X, tap again to place a crown. Marking impossible cells is the key to every solve.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">03</span>
              <h3>No Touching</h3>
              <p>Crowns can never touch each other, not even diagonally. Every crown rules out the eight cells around it.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">04</span>
              <h3>Pattern Recognition</h3>
              <p>Study the given clues and use logical deduction to determine valid crown positions.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">05</span>
              <h3>Victory Condition</h3>
              <p>The puzzle is solved when every row, column and color region holds exactly one crown and no two crowns touch.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">06</span>
              <h3>Progressive Difficulty</h3>
              <p>Start on small 5x5 boards and work up to large expert grids where every deduction counts.</p>
            </div>
          </div>
        </section>

        <section className="gi-section">
          <h2>Advanced Logic Puzzle Solving Techniques</h2>
          <p>
            Mastering <strong>Crowns logic puzzle</strong> requires systematic application of{' '}
            <strong>constraint satisfaction</strong> techniques. Like solving advanced sudoku variants,
            success comes from methodical analysis of logical dependencies and constraint propagation.
          </p>
          <ul className="gi-list">
            <li>Apply constraint propagation to identify forced placements</li>
            <li>Use elimination techniques to reduce candidate possibilities</li>
            <li>When two regions fit inside the same two rows (or columns), clear those rows for every other region</li>
            <li>Work systematically through constraint interactions</li>
            <li>Verify logical consistency at each deduction step</li>
            <li>Apply advanced techniques like naked pairs and hidden singles</li>
          </ul>
        </section>

        <section className="gi-section">
          <h2>Crowns Puzzle FAQ</h2>
          <div className="gi-faq">
            <details open>
              <summary>How many crowns go in each row and column?</summary>
              <p>Exactly one crown per row and per column, like the classic chessboard placement problem.</p>
            </details>
            <details>
              <summary>What is the best starting move?</summary>
              <p>Look for tight regions or rows with limited cells. Those create forced placements and quick eliminations.</p>
            </details>
            <details>
              <summary>Can crowns be adjacent diagonally?</summary>
              <p>No. Crowns cannot touch orthogonally or diagonally, which makes spatial reasoning essential.</p>
            </details>
          </div>
        </section>

        <section className="gi-cta">
          <h2>Ready for Pure Logic Challenge?</h2>
          <p>
            Test your <strong>deductive reasoning</strong> skills with <strong>Crowns logic puzzle</strong> -
            a rigorous <strong>constraint satisfaction</strong> challenge that demands systematic logical
            thinking. Every puzzle has exactly one solution reachable through pure logic alone.
          </p>
          <div className="gi-cta__actions">
            <Link to="/play/crowns" className="button">Play Crowns Now</Link>
            <Link to="/printable/crowns" className="button secondary">Print Puzzles</Link>
            <Link to="/logic-games" className="button secondary">More Logic Puzzles</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CrownsPage;
